import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { KeyRound, Loader2, Mail, ShieldAlert, Lock } from "lucide-react";

import { Button } from "@/components/ui/button";
import { accessRequestCode, accessVerify, accessLogout, apiError } from "@/lib/api";

/**
 * Step 5b: public access screen of a REAL company agent (`GET /api/agents/{slug}` answered with
 * `access_required: true`). Step 1: e-mail -> POST /access/request-code (neutral answer always).
 * Step 2: 6-digit code -> POST /access/verify (the backend sets the httpOnly cookie
 * `iv_access_<company>`; the SPA never stores the token). Demo agents never reach this screen.
 */
export const RESEND_COOLDOWN = 60;
export const NOT_MEMBER_MESSAGE =
  "Su correo no tiene acceso a este agente. Contacte al administrador de su empresa.";
const NEUTRAL_SENT =
  "Si su correo está autorizado, recibirá un código de 6 dígitos. El código vence en 10 minutos.";
const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

// Remembers (per company) which e-mail opened a collaborator session, only to offer "Cerrar sesión";
// the real credential is the httpOnly cookie and the backend checks it on every request.
export const accessFlagKey = (companySlug) => `iv_access_email_${companySlug || ""}`;
const lockKey = (agentSlug, email) => `iv_access_lock_${agentSlug}_${email}`;

const mmss = (s) => `${Math.floor(s / 60)}:${String(Math.max(0, s) % 60).padStart(2, "0")}`;

function useCountdown() {
  const [until, setUntil] = useState(0);
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (until <= Date.now()) return undefined;
    const t = setInterval(() => setNow(Date.now()), 500);
    return () => clearInterval(t);
  }, [until]);
  const left = Math.max(0, Math.ceil((until - now) / 1000));
  return [left, (seconds) => { setNow(Date.now()); setUntil(Date.now() + seconds * 1000); }];
}

function Shell({ agent, children, testid }) {
  const navigate = useNavigate();
  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-950 px-4 py-10 text-slate-100" data-testid={testid}>
      <div className="w-full max-w-md border border-slate-200 bg-white p-8 shadow-xl">
        <button
          onClick={() => navigate("/")}
          className="text-[11px] uppercase tracking-[0.22em] text-slate-500 hover:text-slate-300"
          data-testid="access-home-link"
        >
          Inviktus
        </button>
        <h1 className="mt-4 font-display text-2xl font-medium leading-tight text-slate-50" data-testid="access-agent-name">
          {agent?.name || "Agente"}
        </h1>
        {agent?.company_name && (
          <p className="mt-1 text-xs uppercase tracking-[0.18em] text-accent-blue-light" data-testid="access-company-name">
            {agent.company_name}
          </p>
        )}
        <div className="mt-6">{children}</div>
      </div>
    </div>
  );
}

function Notice({ tone = "info", children, testid }) {
  const cls = {
    info: "border-teal-300 bg-teal-50 text-teal-800",
    error: "border-red-300 bg-red-50 text-red-800",
    warn: "border-amber-400 bg-amber-50 text-amber-900",
  }[tone];
  return (
    <div className={`mt-4 border px-3 py-2.5 text-[13px] leading-snug ${cls}`} role={tone === "error" ? "alert" : "status"} data-testid={testid}>
      {children}
    </div>
  );
}

/** Agent of a deactivated company (existing step 4a message). */
export function AgentInactiveCard({ agent, message }) {
  return (
    <Shell agent={agent} testid="agent-access-inactive">
      <div className="flex items-start gap-3 border border-red-300 bg-red-50 px-4 py-3 text-[13px] leading-snug text-red-800">
        <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0" />
        <span data-testid="agent-access-inactive-message">{message}</span>
      </div>
    </Shell>
  );
}

/** The presented credential (code session or login) has no membership for this agent. */
export function AgentNotMemberCard({ agent, canSwitch, onSwitched }) {
  const [busy, setBusy] = useState(false);
  const switchEmail = async () => {
    setBusy(true);
    try {
      await accessLogout(agent.slug);
    } catch {
      // the session may already be revoked: nothing else to do
    }
    localStorage.removeItem(accessFlagKey(agent.company_slug));
    setBusy(false);
    onSwitched();
  };
  return (
    <Shell agent={agent} testid="agent-access-not-member">
      <div className="flex items-start gap-3 border border-amber-400 bg-amber-50 px-4 py-3 text-[13px] leading-snug text-amber-900">
        <Lock className="mt-0.5 h-4 w-4 shrink-0" />
        <span data-testid="agent-access-not-member-message">{NOT_MEMBER_MESSAGE}</span>
      </div>
      {canSwitch && (
        <Button
          onClick={switchEmail}
          disabled={busy}
          variant="outline"
          className="mt-5 w-full rounded-sm border-slate-700 bg-transparent text-sm text-slate-100 hover:bg-slate-800"
          data-testid="access-switch-email-btn"
        >
          {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null} Cerrar sesión e ingresar con otro correo
        </Button>
      )}
    </Shell>
  );
}

export default function AgentAccessGate({ agent, onGranted, onInactive }) {
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [info, setInfo] = useState(null);
  const [error, setError] = useState(null);
  const [cooldown, startCooldown] = useCountdown();
  const [lockLeft, startLock] = useCountdown();
  const codeRef = useRef(null);
  const verifying = useRef(false);

  const norm = email.trim().toLowerCase();

  useEffect(() => {
    if (step === 2) codeRef.current?.focus();
  }, [step]);

  // a lock (5 wrong codes) survives a reload of the page while it lasts
  useEffect(() => {
    if (step !== 2 || !norm) return;
    const until = Number(localStorage.getItem(lockKey(agent.slug, norm)) || 0);
    if (until > Date.now()) startLock(Math.ceil((until - Date.now()) / 1000));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, norm, agent.slug]);

  const handleCommon = (err) => {
    if (err.code === "company_inactive") {
      onInactive?.(err.detail);
      return true;
    }
    if (err.code === "too_many_requests") {
      setError(err.detail);
      if (err.retryAfter) startCooldown(Math.min(err.retryAfter, 3600));
      return true;
    }
    return false;
  };

  const requestCode = async (e) => {
    e?.preventDefault?.();
    setError(null);
    if (!EMAIL_RE.test(norm)) {
      setError("Indique un correo válido, por ejemplo nombre@empresa.com.");
      return;
    }
    setBusy(true);
    try {
      await accessRequestCode(agent.slug, norm);
      setInfo(NEUTRAL_SENT);
      setCode("");
      setStep(2);
      startCooldown(RESEND_COOLDOWN);
    } catch (ex) {
      const err = apiError(ex, "No se pudo solicitar el código. Intente de nuevo.");
      if (!handleCommon(err)) setError(err.detail);
    } finally {
      setBusy(false);
    }
  };

  const verify = async (value) => {
    const digits = (value ?? code).replace(/\D/g, "").slice(0, 6);
    if (digits.length !== 6) {
      setError("El código tiene 6 dígitos.");
      return;
    }
    if (verifying.current || lockLeft > 0) return;
    verifying.current = true;
    setBusy(true);
    setError(null);
    try {
      await accessVerify(agent.slug, norm, digits);
      localStorage.setItem(accessFlagKey(agent.company_slug), norm);
      localStorage.removeItem(lockKey(agent.slug, norm));
      onGranted(norm);
    } catch (ex) {
      const err = apiError(ex, "No se pudo verificar el código. Intente de nuevo.");
      setCode("");
      if (err.code === "locked") {
        const secs = err.retryAfter || 900;
        localStorage.setItem(lockKey(agent.slug, norm), String(Date.now() + secs * 1000));
        startLock(secs);
        setInfo(null);
        setError(err.detail);
      } else if (err.code === "invalid_code") {
        setError("El código no es correcto o ya caducó (vence a los 10 minutos y solo sirve una vez). Revíselo o solicite uno nuevo.");
      } else if (!handleCommon(err)) {
        setError(err.detail);
      }
      setTimeout(() => codeRef.current?.focus(), 0);
    } finally {
      verifying.current = false;
      setBusy(false);
    }
  };

  const onCodeChange = (raw) => {
    const digits = raw.replace(/\D/g, "").slice(0, 6);
    setCode(digits);
    if (digits.length === 6) verify(digits);
  };

  const locked = lockLeft > 0;

  return (
    <Shell agent={agent} testid="agent-access-gate">
      {step === 1 ? (
        <form onSubmit={requestCode} noValidate data-testid="access-step-email">
          <p className="eyebrow text-slate-400">Paso 1 de 2</p>
          <p className="mt-2 text-sm leading-relaxed text-slate-300">
            Este agente es de uso exclusivo de los colaboradores autorizados de la empresa. Ingrese su correo y le
            enviaremos un código de 6 dígitos. No necesita cuenta ni contraseña.
          </p>
          <label htmlFor="access-email" className="mt-5 block text-[11px] uppercase tracking-[0.18em] text-slate-400">
            Ingrese su correo
          </label>
          <div className="mt-2 flex items-center gap-2 border border-slate-300 px-3 focus-within:border-accent-blue">
            <Mail className="h-4 w-4 text-slate-500" />
            <input
              id="access-email"
              type="email"
              autoComplete="email"
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="nombre@empresa.com"
              className="h-11 w-full bg-transparent text-[15px] text-slate-100 outline-none placeholder:text-slate-600"
              data-testid="access-email-input"
            />
          </div>
          {error && <Notice tone="error" testid="access-error">{error}</Notice>}
          <Button
            type="submit"
            disabled={busy || !norm || cooldown > 0}
            className="mt-5 h-11 w-full rounded-sm bg-slate-900 text-sm font-medium text-white hover:bg-slate-800"
            data-testid="access-request-code-btn"
          >
            {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            {cooldown > 0 ? `Espere ${mmss(cooldown)}` : "Recibir código"}
          </Button>
        </form>
      ) : (
        <form onSubmit={(e) => { e.preventDefault(); verify(); }} noValidate data-testid="access-step-code">
          <p className="eyebrow text-slate-400">Paso 2 de 2</p>
          <p className="mt-2 text-sm leading-relaxed text-slate-300">
            Escriba el código de 6 dígitos enviado a <span className="font-medium text-slate-100" data-testid="access-email-shown">{norm}</span>.
          </p>
          {info && !error && <Notice tone="info" testid="access-info">{info}</Notice>}
          <label htmlFor="access-code" className="mt-5 block text-[11px] uppercase tracking-[0.18em] text-slate-400">
            Código de acceso
          </label>
          <div className="mt-2 flex items-center gap-2 border border-slate-300 px-3 focus-within:border-accent-blue">
            <KeyRound className="h-4 w-4 text-slate-500" />
            <input
              id="access-code"
              ref={codeRef}
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              pattern="[0-9]*"
              maxLength={12}
              autoFocus
              disabled={locked}
              value={code}
              onChange={(e) => onCodeChange(e.target.value)}
              onPaste={(e) => {
                e.preventDefault();
                onCodeChange(e.clipboardData.getData("text") || "");
              }}
              placeholder="000000"
              aria-describedby="access-code-help"
              className="h-12 w-full bg-transparent font-mono text-2xl tracking-[0.5em] text-slate-100 outline-none placeholder:text-slate-700 disabled:opacity-50"
              data-testid="access-code-input"
            />
          </div>
          <p id="access-code-help" className="mt-2 text-[12px] text-slate-500">
            Puede pegar el código completo. Vence a los 10 minutos y solo puede usarse una vez.
          </p>
          {error && <Notice tone={locked ? "warn" : "error"} testid="access-error">{error}</Notice>}
          {locked && (
            <Notice tone="warn" testid="access-locked">
              Por seguridad, el ingreso con este correo está bloqueado durante {mmss(lockLeft)}. Después solicite un código nuevo.
            </Notice>
          )}
          <Button
            type="submit"
            disabled={busy || locked || code.length !== 6}
            className="mt-5 h-11 w-full rounded-sm bg-slate-900 text-sm font-medium text-white hover:bg-slate-800"
            data-testid="access-verify-btn"
          >
            {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null} Ingresar
          </Button>
          <div className="mt-4 flex items-center justify-between gap-3 text-[12px]">
            <button
              type="button"
              onClick={() => { setStep(1); setError(null); setInfo(null); setCode(""); }}
              className="text-slate-400 underline-offset-4 hover:text-slate-200 hover:underline"
              data-testid="access-change-email-btn"
            >
              Cambiar correo
            </button>
            <button
              type="button"
              onClick={requestCode}
              disabled={busy || cooldown > 0 || locked}
              className="text-accent-blue underline-offset-4 hover:underline disabled:cursor-not-allowed disabled:text-slate-500 disabled:no-underline"
              data-testid="access-resend-btn"
            >
              {cooldown > 0 ? `Reenviar código en ${mmss(cooldown)}` : "Reenviar código"}
            </button>
          </div>
        </form>
      )}
    </Shell>
  );
}
